import { Col, Row, Spin } from "antd";
import { BsCartPlusFill } from "react-icons/bs";
import { FaCheck } from "react-icons/fa6";
import { HiOutlineEye, HiOutlineShoppingBag } from "react-icons/hi2";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../apis/store";
import star from "../../../styles/images/star.svg";
import useIsConnected from "../../../utils/IsConnected";
import { useTranslation } from "react-i18next";
import { setWalletAmount } from "../../../apis/slices/globalSlice";
import { entityIdsEqual } from "../../../apis/slices/utils";
import { buildDetailNavState } from "../../../utils/shopListReturn";
import { CART_ADDED_RESULT_EVENT, type CartAddedResult } from "../../../utils/cartAddedFeedback";

interface Props {
  id: number;
  name: string;
  description: string;
  description_website: string;
  review: number;
  reviewCount?: number | string | null;
  totalSalesCount?: number | string | null;
  stockAvailable?: boolean | null;
  img: string;
  price: any;
  discount: any;
  type: "product" | "service";
  toAddService?: (added: boolean) => void;
  toAddProduct?: (added: boolean) => void;
}

function plainTextFromHtml(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function parsePriceValue(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/,/g, ""));
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

const CardItem = ({
  id,
  name,
  description,
  review,
  reviewCount,
  totalSalesCount,
  stockAvailable,
  img,
  price,
  discount,
  type,
  description_website,
  toAddService,
  toAddProduct,
}: Props) => {
  const { products, services } = useSelector(
    (state: RootState) => state?.global
  );
  const addShopLoading = useSelector(
    (state: RootState) => state?.services?.AddShop?.loading ?? false
  );
  const [justAdded, setJustAdded] = useState(false);
  const [adding, setAdding] = useState(false);
  const awaitingResult = useRef(false);
  const addedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const onResult = (event: Event) => {
      if (!awaitingResult.current) return;
      const result = (event as CustomEvent<CartAddedResult>).detail;
      if (result.item?.type !== "product" || !entityIdsEqual(result.item.id, id)) return;
      awaitingResult.current = false;
      setAdding(false);
      const success = result.success;
      setJustAdded(success);
      if (addedTimer.current !== null) clearTimeout(addedTimer.current);
      if (success) addedTimer.current = setTimeout(() => setJustAdded(false), 2000);
    };
    window.addEventListener(CART_ADDED_RESULT_EVENT, onResult);
    return () => {
      window.removeEventListener(CART_ADDED_RESULT_EVENT, onResult);
      if (addedTimer.current !== null) clearTimeout(addedTimer.current);
    };
  }, [id]);
  const { t } = useTranslation();
  const isConnected = useIsConnected();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const detailState = useMemo(
    () => ({
      id,
      name,
      description,
      review,
      img,
      price,
      type,
      description_website,
    }),
    [id, name, description, review, img, price, type, description_website]
  );

  const goDetail = useCallback(() => {
    navigate("/detail", {
      state: buildDetailNavState({
        id,
        type,
        returnTo: type === "service" ? "/services" : "/products",
        name,
        price,
      }),
    });
  }, [id, name, navigate, price, type]);

  const onImageKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goDetail();
    }
  };

  const toAddToCart = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (addShopLoading || adding) return;
    if (!isConnected) {
      navigate("/login");
      return;
    }
    if (type === "product") {
      if (!toAddProduct) return;
      awaitingResult.current = true;
      setAdding(true);
      setJustAdded(false);
      toAddProduct?.(
        products.some((el: any) => entityIdsEqual(el?.id, id))
      );
    } else {
      toAddService?.(
        services.some((el: any) => entityIdsEqual(el?.id, id))
      );
    }
    dispatch(setWalletAmount(0));
  };

  const descriptionSource = useMemo(
    () => [description, description_website].filter(Boolean).join(" ") || "",
    [description, description_website]
  );

  const descriptionBodyPlain = useMemo(
    () => plainTextFromHtml(descriptionSource),
    [descriptionSource]
  );

  const salesCount =
    typeof totalSalesCount === "number" ||
    (typeof totalSalesCount === "string" && totalSalesCount.trim() !== "")
      ? Number(totalSalesCount)
      : NaN;
  const hasSalesCount = Number.isSafeInteger(salesCount) && salesCount > 0;
  const ratingValue = Number(review);
  const normalizedRating = Number.isFinite(ratingValue)
    ? Math.min(5, Math.max(0, ratingValue))
    : 0;
  const ratingText = normalizedRating.toFixed(1);
  const parsedReviewCount = Number(reviewCount);
  const normalizedReviewCount =
    Number.isSafeInteger(parsedReviewCount) && parsedReviewCount >= 0
      ? parsedReviewCount
      : 0;
  const hasReviews = normalizedReviewCount > 0 && normalizedRating > 0;
  const isProduct = type === "product";
  const listPriceNum = parsePriceValue(price);
  const salePriceNum = parsePriceValue(discount);
  const hasSale =
    listPriceNum > 0 &&
    salePriceNum > 0 &&
    salePriceNum < listPriceNum;

  if (isProduct) {
    return (
      <div className="card-journey-small background-card ac-product-card ac--font-expo">
        <button
          type="button"
          className="ac-product-card__media"
          onClick={goDetail}
          onKeyDown={onImageKeyDown}
          aria-label={`${t("services.moreDetails")}: ${name}`}
        >
          {hasSale ? (
            <span className="ac-product-card__ribbon">
              {t("services.productCard.onSale")}
            </span>
          ) : null}
          {img ? (
            <img src={img} alt="" loading="lazy" />
          ) : (
            <img
              src="assets/imgs/shop/shop-list/product2.png"
              alt=""
              loading="lazy"
            />
          )}
          <span className="ac-product-card__media-shade" aria-hidden />
          <span className="ac-product-card__media-hover" aria-hidden>
            <span className="ac-product-card__view-cta">
              <HiOutlineEye className="ac-product-card__view-icon" aria-hidden />
            </span>
          </span>
        </button>

        <div className="ac-product-card__body">
          <h3 className="ac-product-card__title">{name}</h3>

          <p
            className={`ac-product-card__desc${
              descriptionBodyPlain ? "" : " ac-product-card__desc--empty"
            }`}
            aria-hidden={descriptionBodyPlain ? undefined : true}
          >
            {descriptionBodyPlain || "\u00a0"}
          </p>

          <div className="ac-product-card__tag-stack">
            {hasSalesCount && <div className="ac-product-card__sales">
              <HiOutlineShoppingBag aria-hidden="true" />
              <span>
                {t("services.productCard.sales", {
                  value: String(salesCount),
                })}
              </span>
            </div>}
            {hasReviews && <div
              className="ac-product-card__rating-summary"
              aria-label={t("services.productCard.ratingAria", {
                rating: ratingText,
                count: normalizedReviewCount,
              })}
            >
              <span className="ac-product-card__rating-star" aria-hidden="true">
                ★
              </span>
              <span className="ac-product-card__tag-num">{ratingText}</span>
              <span className="ac-product-card__review-count">
                {t("services.productCard.reviewCount", {
                  count: normalizedReviewCount,
                })}
              </span>
            </div>}
            {stockAvailable !== null && stockAvailable !== undefined && <span
              className={`ac-product-card__tag ac-product-card__tag--stock ${
                stockAvailable
                  ? "ac-product-card__tag--stock-in"
                  : "ac-product-card__tag--stock-out"
              }`}
            >
              {stockAvailable ? t("services.stock.in") : t("services.stock.out")}
            </span>}
          </div>

          <div className="ac-product-card__price-row">
            <span className="ac-product-card__price-label">
              {t("services.productCard.priceLabel")}
            </span>
            <div className="ac-product-card__price-values">
              {hasSale ? (
                <>
                  <p className="ac-product-card__price-current icon-saudi_riyal mb-0">
                    {discount}
                  </p>
                  <p className="ac-product-card__price-was icon-saudi_riyal mb-0">
                    {price}
                  </p>
                </>
              ) : (
                <p className="ac-product-card__price-current icon-saudi_riyal mb-0">
                  {price}
                </p>
              )}
            </div>
          </div>

          <div className="ac-product-card__footer">
            <button
              type="button"
              className={`ac-product-card__add${justAdded ? " ac-product-card__add--added" : ""}`}
              onClick={toAddToCart}
              disabled={addShopLoading || adding}
              aria-busy={addShopLoading || adding}
            >
              <span className="ac-product-card__add-icon" aria-hidden>
                {justAdded ? (
                  <FaCheck size={15} />
                ) : addShopLoading || adding ? (
                  <Spin size="small" />
                ) : (
                  <BsCartPlusFill size={15} />
                )}
              </span>
              <span className="ac-product-card__add-label" role="status" aria-live="polite" aria-atomic="true">
                {t(justAdded ? "services.productCard.added" : "services.cart")}
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-journey-small background-card hover-up">
      <div className="card-image" style={{ minHeight: "10rem" }}>
        <span className="wish ac--pointer" onClick={() => toAddToCart()}>
          <BsCartPlusFill fill={"var(--ac-theme-accent)"} color={"#fff"} size={"20"} />
        </span>
        {img ? (
          <img src={img} alt={name} />
        ) : (
          <img src="assets/imgs/shop/shop-list/product2.png" alt={name} />
        )}
      </div>
      <div className="card-info p-3 pt-30 border-top">
        <div className="card-rating">
          <div className="card-left"></div>
          <div className="card-right">
            <span className="rating text-xs-medium rounded-pill">
              {review}
              <span className="text-xs-medium neutral-500">
                <Row gutter={4} justify={"space-between"}>
                  <Col>
                    <img width={14} src={star} />
                  </Col>
                  <Col>
                    <img width={14} src={star} />
                  </Col>
                  <Col>
                    <img width={14} src={star} />
                  </Col>
                  <Col>
                    <img width={14} src={star} />
                  </Col>
                  <Col>
                    <img width={14} src={star} />
                  </Col>
                </Row>
              </span>
            </span>
          </div>
        </div>
        <div className="card-title" style={{ height: "80px" }}>
          <span className="text-lg-bold neutral-1000">{name}</span>
        </div>
        <div className="card-program">
          <div
            className="list-style-disc ps-3 pe-4 ml-10"
            style={{ minHeight: "5rem" }}
          >
            {description ? (
              <div
                className="description-text"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            ) : (
              <></>
            )}
          </div>
          {isConnected && (
            <>
              <div className="endtime pt-3 mt-3 border-top">
                <div className="card-price"></div>
                {stockAvailable === true ? (
                  <span className="ac--text-stock ac--font-expo">
                    {t("services.stock.in")}
                  </span>
                ) : stockAvailable === false ? (
                  <span className="ac--text-out-stock ac--font-expo">
                    {t("services.stock.out")}
                  </span>
                ) : null}
              </div>
              <div className="endtime">
                <div className="card-price">
                  {discount > 0 && discount !== price ? (
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="text-lg-bold neutral-1000 text-decoration-line-through icon-saudi_riyal">
                        {price}
                      </h6>
                      <h6 className="text-lg-bold neutral-1000 icon-saudi_riyal">
                        {discount}
                      </h6>
                    </div>
                  ) : (
                    <h6 className="text-lg-bold neutral-1000 icon-saudi_riyal">
                      {price}
                    </h6>
                  )}
                </div>
                <div className="card-button"></div>
              </div>
            </>
          )}
          <div className="endtime">
            <div className="card-price">
              <button
                className="btn  btn-gray rounded-3 gap-2 button-add-to-cart"
                onClick={goDetail}
              >
                {t("profile.order.details")}
              </button>
            </div>
            <div className="card-button">
              <button
                type="button"
                onClick={() => toAddToCart()}
                className="btn ac--btn-card-item rounded-3 gap-2 button-add-to-cart ac--font-expo"
                disabled={addShopLoading}
                aria-busy={addShopLoading}
              >
                {addShopLoading ? (
                  <Spin size="small" className="me-1" />
                ) : null}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <g clipPath="url(#clip0_20_23536)">
                    <path
                      d="M16 2H2.828L2.8 1.766C2.7427 1.27961 2.50892 0.831155 2.14299 0.505652C1.77706 0.180149 1.30442 0.000227862 0.814667 0L0 0V1.33333H0.814667C0.977956 1.33335 1.13556 1.3933 1.25758 1.50181C1.3796 1.61032 1.45756 1.75983 1.47667 1.922L2.53333 10.9007C2.59063 11.3871 2.82441 11.8355 3.19034 12.161C3.55627 12.4865 4.02891 12.6664 4.51867 12.6667H13.3333V11.3333H4.51867C4.35528 11.3333 4.19759 11.2733 4.07555 11.1646C3.95351 11.056 3.87562 10.9063 3.85667 10.744L3.76933 10H14.5573L16 2ZM13.4427 8.66667H3.61267L2.98533 3.33333H14.4047L13.4427 8.66667Z"
                      fill="#101010"
                    />
                    <path
                      d="M4.66732 15.9997C5.4037 15.9997 6.00065 15.4027 6.00065 14.6663C6.00065 13.93 5.4037 13.333 4.66732 13.333C3.93094 13.333 3.33398 13.93 3.33398 14.6663C3.33398 15.4027 3.93094 15.9997 4.66732 15.9997Z"
                      fill="#101010"
                    />
                    <path
                      d="M11.3333 15.9997C12.0697 15.9997 12.6667 15.4027 12.6667 14.6663C12.6667 13.93 12.0697 13.333 11.3333 13.333C10.597 13.333 10 13.93 10 14.6663C10 15.4027 10.597 15.9997 11.3333 15.9997Z"
                      fill="#101010"
                    />
                  </g>
                </svg>
                {t("services.cart")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardItem;
